/**
 * withTimeout
 *
 * Races a promise against a timeout so a stalled Firestore connection (or any
 * other hung async call) can never block the UI indefinitely. Generalised from
 * the inline `getDocsWithTimeout` helper that previously lived in
 * MailshotDetailPage.js.
 *
 * @param {Promise<T>} promise - The promise to guard.
 * @param {number} [timeoutMs=15000] - How long to wait before rejecting.
 * @param {string} [message] - Error message used when the timeout fires.
 * @returns {Promise<T>} Resolves/rejects with the original promise, or rejects
 *   with a timeout error if it doesn't settle in time.
 */
export function withTimeout(
  promise,
  timeoutMs = 15000,
  message = "Request timed out. Your browser cache may be stale — try a hard refresh (Cmd+Shift+R)."
) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), timeoutMs);
  });

  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

export default withTimeout;
